import React, { useEffect, useState } from "react";
import { Image, Menu, Message, Popover } from "@arco-design/web-react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  IconCaretDown,
  IconLeft,
  IconRight,
} from "@arco-design/web-react/icon";
import _ from "lodash";
import cs from "classnames";
import "./index.less";
import RolePermission from "@/components/business/RolePermission";
import User from "@/stores/userStore";
import { coming_soon_icon } from "@/assets/user-center";
import StructuringModal from "@/components/business/StructuringModal";
import CreateEngineeringModal from "@/pages/CreationSpace/CreativeSpace/CreativeContainer/components/Engineering/CreatEngineeringModal/CreateEngineeringModal";
import {
  createEngineering,
  deleteEnginering,
  editEngineering,
} from "@/services/createSpace/engineering";
import addEngineeringImg from "@/assets/create-space/add-project.png";
import useRequest from "@/requestHooks/mixCutDetail/useRequest";
import {
  getEngineerList,
  IGraphQEngineerItem,
  SearchEngineerQuery,
} from "@/services/createSpace/graphQ/getEngineerList";
import useNavProject from "@/useStore/createspace/useProject";
import appStore from "@/stores/appStore";
import useNavigateTo from "@/utils/useNavigateTo";
import MatrixDownloadModal from "../MatrixDownloadModal";

const { Item: MenuItem, SubMenu } = Menu;

type ChildMenuItemType = {
  id: string;
  subTitle: string;
  path: string;
  power?: boolean;
  font?: boolean;
  permissions?: string;
  hidden?: boolean;
};

type MenuItemType = {
  id: string;
  title: string;
  path?: string;
  icon: string[];
  hot: boolean;
  font?: boolean;
  permissions?: string;
  children?: ChildMenuItemType[];
};

type MenuItemPathInfo = {
  parent: MenuItemType;
  child?: ChildMenuItemType;
};

type Props = {
  menuList: MenuItemType[];
};

const Index: React.FC<Props> = ({ menuList }) => {
  const { navigateTo } = useNavigateTo();
  const [collapse, setCollapse] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [isShowCreateModal, setIsShowCreateModal] = useState<boolean>(false);
  const [createModalVisible, setCreateModalVisible] = useState<boolean>(false);
  const [createModalType, setCreateModalType] = useState<
    "imageText" | "normal"
  >(); // 新建工程的类型，是否是图文工程
  const [isShowCreateEngineering, setIsShowCreateEngineering] =
    useState<boolean>(false);
  const { projectId } = useNavProject();
  const {
    loading,
    data,
    pageParams,
    handleRequest,
    searchData,
    setSearchData,
    handleBatchDelete,
    handlePageChange,
    handleCreateItem,
    handleUpdateItem,
  } = useRequest<IGraphQEngineerItem, SearchEngineerQuery>({
    request: getEngineerList,
    initSearchData: {
      project_id: projectId,
      keyword: "",
      sort_sc: "desc",
      sort_field: "created_at",
    },
    page: {
      page: 1,
      page_size: 20,
    },
    createItem: createEngineering,
    batchDelete: deleteEnginering,
    updateItem: editEngineering,
    manual: true,
  });
  const [visible, setVisible] = useState(false)
  // 处理导航和权限检查
  const handleNavigation = (item: MenuItemType | ChildMenuItemType) => {
    if (!User.isExpired_at_package) {
      User.setExclusiveModal(true);
      return;
    }
    if (!User.dataIntegration?.benefits?.find((v) => {
      return v.benefit_type == 1;
    })?.benefit_value) {
      Message.warning("您的套餐权益为空，无法使用");
      return;
    }
    if (item.id === '7-2') {
      setVisible(true)
      return;
    }
    if (item.path && item.path !== "/") {
      if ((item as any).blank) {
        // window.open(item.path, "_blank");
        navigateTo(item.path);
      } else {
        if (item.path === "/creation-script") {
          setIsShowCreateModal(true);
        } else if (item.path === "/image-text") {
          setCreateModalType("imageText");
          setCreateModalVisible(true);
        } else if (item.path === "/mix-detail") {
          setCreateModalType("normal");
          setCreateModalVisible(true);
        } else {
          navigate(item.path);
        }
      }
    } else {
      Message.info({
        icon: <img width={26} src={coming_soon_icon} alt="敬请期待" />,
        content: "敬请期待呦~",
      });
    }
  };

  // 根据路径获取对应的菜单项
  const getMenuItemByPath = (
    targetPath: string
  ): MenuItemPathInfo | undefined => {
    for (const item of menuList) {
      if (targetPath.includes(item.path)) {
        return { parent: item };
      }
      if (item.children) {
        const child = item.children.find((child) => child.path === targetPath);
        if (child) return { parent: item, child };
      }
    }
    return undefined;
  };

  // 初始化选中的菜单项
  useEffect(() => {
    if (location.pathname !== "/") {
      const pathSegment = `/${location.pathname.split("/")[1]}`;
      const pathInfo = getMenuItemByPath(pathSegment);
      if (pathInfo) {
        const parentId = pathInfo.parent.id.split("-")[0];
        setOpenKeys([parentId]);
        setSelectedKeys([
          pathInfo.child ? pathInfo.child.id : pathInfo.parent.id,
        ]);
      }
    }
  }, [location.pathname, menuList]);

  // 设置Logo宽度
  useEffect(() => {
    User.setLogoWidth(collapse ? 107 : 166);
  }, [collapse]);

  // 渲染Popover内容
  const renderPopoverContent = (item: MenuItemType) => {
    if (!item.children) return null;

    return (
      <div className="menu-item-c-collapse">
        {item.children
          .filter((item) => !item.hidden)
          .map((child: any) => (
            <RolePermission.Unclickable
              key={child.id}
              mixture={item.title === "团队管理"}
              requiredPermissions={child.permissions}
            >
              <div
                className="menu-item-c-collapse-item"
                onClick={() => handleNavigation(child)}
              >
                <div
                  style={{ background: child.icon?.color }}
                  className="menu-item-c-collapse-svg"
                >
                  <img src={child.icon?.svg} width={20} alt={child.subTitle} />
                </div>
                <p
                  style={{
                    color: selectedKeys[0] === child.id ? "#3662ec" : undefined,
                  }}
                  className="menu-item-c-collapse-text"
                >
                  {child.subTitle}
                </p>
              </div>
            </RolePermission.Unclickable>
          ))}
      </div>
    );
  };

  // 处理折叠菜单项点击事件
  const handleCollapseClick = (item: MenuItemType, event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (item.children?.length) {
      return;
    }
    handleNavigation(item);
  };

  return (
    <div className="menu-root">
      <MatrixDownloadModal visible={visible} setVisible={setVisible}/>
      <StructuringModal
        handleChangeModal={() => {
          setIsShowCreateModal(false);
        }}
        isShowModal={isShowCreateModal}
      />
      {createModalVisible ? (
        <CreateEngineeringModal
          submit={async (e) => {
            if (createModalType === "imageText") {
              if (!e.id) {
                try {
                  const data = await handleCreateItem({
                    ...e,
                    type: 3,
                  });
                  // window.open(`/image-text?id=${data.id}`, "_blank");
                  navigateTo(`/image-text?id=${data.id}`);
                } catch (e) {
                  console.log(e);
                }
              } else {
                handleUpdateItem(e);
              }
            } else {
              const data = await createEngineering(e);
              navigateTo(`/mix-detail/${data.id}`);
            }
            setCreateModalVisible(false);
          }}
          title={
            <div className="engineer-title-wrapper">
              <img src={addEngineeringImg}></img>
              {createModalType === "imageText" ? "新建图文工程" : "新建工程"}
            </div>
          }
          value={{}}
          visible={createModalVisible}
          onCancel={() => {
            setCreateModalVisible(false);
          }}
          onOk={(e) => {
            setCreateModalVisible(false);
          }}
          type={3}
        />
      ) : (
        <></>
      )}
      {/* 折叠按钮 */}
      <div className="collapse-box" onClick={() => setCollapse(!collapse)}>
        {collapse ? <IconRight /> : <IconLeft />}
      </div>

      {/* 折叠状态下的菜单 */}
      {collapse ? (
        <div
          className="menu-container-collapse"
          style={{
            background: !appStore.isOdm
              ? ""
              : "linear-gradient(180deg, #dfeaff 0%, #F4F8FF 100%)",
          }}
        >
          {menuList.map((item) => (
            <RolePermission.Unclickable
              key={item.id}
              requiredPermissions={item.permissions}
              mixture={item.title === "团队管理"}
            >
              <div
                className="menu-item-collapse"
                style={{
                  marginTop: item.path === "/task-center" ? "auto" : 0,
                  position:
                    item.path === "/task-center" ? "relative" : "static",
                  top: item.path === "/task-center" ? 16 : 0,
                }}
                onClick={(e) => handleCollapseClick(item, e)}
              >
                <Popover position="right" content={renderPopoverContent(item)}>
                  <div className="menu-item-collapse-text">
                    <div className="menu-icon">
                      <Image
                        src={
                          selectedKeys[0]?.split("-")[0] === item.id
                            ? item.icon[1]
                            : item.icon[0]
                        }
                        preview={false}
                        width={26}
                        alt={item.title}
                      />
                      {item.hot && (
                        <div className="menu-hot">
                          <span>HOT</span>
                        </div>
                      )}
                    </div>
                    <p
                      style={{
                        color:
                          selectedKeys[0]?.split("-")[0] === item.id
                            ? "#3662ec"
                            : selectedKeys[0] === item.id
                              ? "#3662ec"
                              : undefined,
                      }}
                      className={cs(item.font && "DOUYU-text")}
                    >
                      {item.title}
                    </p>
                  </div>
                </Popover>
              </div>
            </RolePermission.Unclickable>
          ))}
        </div>
      ) : (
        /* 展开状态下的菜单 */
        <div
          className="menu-container"
          style={{
            width: collapse ? 106 : 166,
            background: !appStore.isOdm
              ? ""
              : "linear-gradient(180deg, #dfeaff 0%, #F4F8FF 100%)",
          }}
        >
          <Menu
            theme="light"
            icons={{ horizontalArrowDown: <IconCaretDown /> }}
            collapse={collapse}
            openKeys={openKeys}
            selectedKeys={selectedKeys}
            onClickSubMenu={(key, _, keyPath) => {
              setOpenKeys(keyPath[0] === openKeys[0] ? [] : keyPath);
            }}
            onClickMenuItem={(key) => {
              setSelectedKeys([key]);
            }}
          >
            {menuList.map((item) => {
              const isTeamManagement = item.title === "团队管理";
              const hasSuperAdminRole = (
                User.teamInfo?.team_user_role?.roles || []
              ).some((role) => role.code === "superadmin");

              // 如果是团队管理且用户不是超级管理员，则不显示
              if (isTeamManagement && !hasSuperAdminRole) {
                return null;
              }

              if (item.children?.length) {
                return (
                  <SubMenu
                    key={item.id}
                    className="layout-subMenu"
                    title={
                      <div
                        className={cs(!collapse && "menu-flex")}
                        style={{ textAlign: "center" }}
                      >
                        <div
                          className={cs("menu-icon", !collapse && "menu-flex")}
                        >
                          <Image
                            src={
                              selectedKeys[0]?.split("-")[0] === item.id
                                ? item.icon[1]
                                : item.icon[0]
                            }
                            preview={false}
                            width={26}
                            alt={item.title}
                          />
                          {item.hot && (
                            <div className="menu-hot">
                              <span>HOT</span>
                            </div>
                          )}
                        </div>
                        {!collapse && (
                          <div
                            className={cs(
                              "menu-flex",
                              item.font && "DOUYU-text"
                            )}
                          >
                            {item.title}
                          </div>
                        )}
                      </div>
                    }
                  >
                    {item.children
                      .filter((p) => !p.hidden)
                      .map((child) => {
                        if (child.power) return null;
                        return (
                          <RolePermission.Unclickable
                            key={child.id}
                            requiredPermissions={child.permissions}
                          >
                            <MenuItem
                              key={child.id}
                              className="menu-item"
                              onClick={() => handleNavigation(child)}
                            >
                              {child.subTitle}
                            </MenuItem>
                          </RolePermission.Unclickable>
                        );
                      })}
                  </SubMenu>
                );
              }

              return (
                <RolePermission.Unclickable
                  key={item.id}
                  requiredPermissions={item.permissions}
                >
                  <div
                    className="layout-subMenu"
                    style={{
                      marginTop: item.path === "/task-center" ? "auto" : 0,
                      paddingTop: item.path === "/task-center" ? 16 : 0,
                    }}
                  >
                    <MenuItem
                      key={item.id}
                      className={cs(
                        !collapse ? "menu-flex" : "menu-item no-children"
                      )}
                      onClick={() => {
                        setOpenKeys([]);
                        setSelectedKeys([item.id]);
                        handleNavigation(item);
                      }}
                    >
                      <div
                        className={cs("menu-icon", !collapse && "menu-flex")}
                      >
                        <Image
                          src={item.icon[selectedKeys[0] === item.id ? 1 : 0]}
                          preview={false}
                          width={26}
                          style={{ marginLeft: !collapse ? 12 : 0 }}
                          alt={item.title}
                        />
                        {item.hot && (
                          <div className="menu-hot">
                            <span>HOT</span>
                          </div>
                        )}
                      </div>
                      {!collapse && (
                        <div className={cs(item.font && "DOUYU-text")}>
                          <span>{item.title}</span>
                        </div>
                      )}
                    </MenuItem>
                  </div>
                </RolePermission.Unclickable>
              );
            })}
          </Menu>
        </div>
      )}
    </div>
  );
};

export default Index;
